import dayjs from 'dayjs';
import React from 'react';
import Calendar from './calendar/Calendar';
import Config from '../util/config';
import { getDeepLink } from '../util/deep-links';
import MonthSelect from './MonthSelect';
import NightSelect from './NightSelect';
import { getPriceData, Price, PriceResponseData, getStartDates, AvailabilityData, DateAvailability, getEndDates, EndDateResponse, EndDate } from '../service/prices';
import { CartData } from '../service/cart';
import { getEndDate, getStartDate } from '../util/date';
import Loader from './Loader';
import Popup from './Popup';
import Step1 from './checkout/Step1';
import Step2 from './checkout/Step2';
import PriceInfo from './PriceInfo';
import { dispatchEvent } from '../util/dispatch-event'

export interface Props {
}

export interface State {
  start: string; // start month
  loading: boolean;
  error: boolean;
  errorMessage: string;
  selected: string; // start date
  notBooked: string[];
  prices: Price[];
  selectedNights: number;
  showCheckout: boolean
  data: CartData
  calendarsToDisplay: number;
  displayButton: boolean;
  selectableDates: string[];
  nightsSelectorPosition: string;
  alwaysDisplayNightSelector: boolean
  arrivals: string[]
  selectedPrice: Price | null;
  selectableNights: number[];
  selectablePrices: Price[];
}

class App extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.onMonthChange = this.onMonthChange.bind(this);
    this.onDaySelect = this.onDaySelect.bind(this);
    this.onNightSelect = this.onNightSelect.bind(this);
    this.openBookingForm = this.openBookingForm.bind(this);

    this.state = {
      start: Config.get('start', dayjs('YYYY-MM')),
      selected: Config.get('selected', ''),
      selectedNights: Config.get('nights', 0),
      selectedPrice: null,
      loading: true,
      error: false,
      errorMessage: '',
      notBooked: [],
      prices: [],
      showCheckout: false,
      data: null,
      calendarsToDisplay: Config.get('calendarsToDisplay', 1),
      displayButton: Config.get('alwaysDisplayBookButton') == 'yes',
      nightsSelectorPosition: Config.get('nightPosition', 'bottom'),
      alwaysDisplayNightSelector: Config.get('alwaysDisplayNightSelector', 'no') == 'yes',
      selectableDates: [],
      selectableNights: [],
      arrivals: [],
      selectablePrices: [],
    };

    if (this.state.selected && this.state.selectedNights) {
      this.state = {
        ...this.state,
        data: this.getData(),
      }
    }
  }

  getData() {
    return {
      calendar_key: Config.get('calendarKey'),
      properties: [
        {
          arrival: {
            date: dayjs(this.state.selected).format('YYYY-MM-DD'),
          },
          cottage_id: Config.get('propertyCode'),
          departure: {
            date: dayjs(this.state.selected).add(this.state.selectedNights, 'days').format('YYYY-MM-DD')
          },
          guests: {
            adults: 1,
            children: 0,
            infants: 0,
          },
        }
      ]
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // When only a start date is passed to calendar with no end date/nights
    // this makes sure that available end dates are clickable 
    // Need to wait for prices to be available
    if (
      prevState.prices.length === 0 && 
      this.state.prices.length !== 0 && 
      this.state.selected && 
      !this.state.selectedNights
    ) {
      this.onDaySelect(dayjs(this.state.selected));
    }

    if (
      !prevState ||
      (prevState.selected === this.state.selected &&
        prevState.selectedNights === this.state.selectedNights)
    )
      return

    this.setState({
      ...this.state,
      data: this.getData(),
    })
  }


  loadPrices(): void {
    const start = getStartDate(
      dayjs(this.state.start + '-01', 'YYYY-MM-DD'),
      Config.get('startingDay', 'Mon')
    );
    const end = getEndDate(
      dayjs(this.state.start + '-01', 'YYYY-MM-DD').add(this.state.calendarsToDisplay - 1, 'month'),
      Config.get('startingDay', 'Mon')
    );

    const params = new URLSearchParams(window.location.search);
    const usePricingApi = Config.get('useCalendarApi', 'no') == 'yes'

    if (usePricingApi) {
      getStartDates(Config.get('propertyCode'),
        start,
        end,
        Config.get('calendarKey')
      ).then((data: AvailabilityData) => {
        let arrivalDates: string[] = []
        let nonBooked: string[] = []
        Object.values(data).map((date: DateAvailability) => {
          if (date.arrival) {
            arrivalDates.push(date.dateTime)
          }

          if (!date.booked) {
            nonBooked.push(date.dateTime)
          }
        })
        this.setState({
          arrivals: arrivalDates,
          notBooked: nonBooked
        })
      }
      ).catch((reason: string) => {
        this.setState({
          loading: false,
          error: true,
          errorMessage: reason,
        });
      })
    }
    getPriceData(
      Config.get('propertyCode'),
      start.subtract(40, 'days'),
      end,
      params.get('sleeps')
    ).then((data: PriceResponseData) => {
      const updateStateItems = !this.state.prices.length;
      this.setState({
        loading: false,
        prices: data.prices,
      });

      if (!usePricingApi) {
        this.setState({
          notBooked: data.not_booked
        })
      }

      if (!updateStateItems || !this.state.selected) {
        return;
      }
      this.setState({
        selectablePrices: this.generateFilteredPrices(this.state.selected),
      })
      if (Config.get('calendar_selector', 'default') == 'default') {
        this.setState({
          selectableNights: this.generateNights(this.state.selected),
        });
      }
      if (this.state.selectedNights) {
        this.setState({
          selectedPrice: this.generatePrice(this.state.selectedNights),
        });
      }

    }).catch((reason: string) => {
      this.setState({
        loading: false,
        error: true,
        errorMessage: reason,
      });
    });

  }

  onMonthChange(start: string): void {
    this.setState({
      start,
      loading: true,
    });
  }

  onDaySelect(day: dayjs.Dayjs): void {
    const calendarType = Config.get('calendar_selector', 'default');
    const { selected } = this.state;

    // Range selector
    if (calendarType === 'range') {
      this.loadPrices();
      this.selectRange(day);
    } else {
      this.selectNights(day);
    }
  }

  generateFilteredPrices(selected: string) {
    const { prices } = this.state;
    return prices.filter((value) => {
      return value.start_date === selected;
    });
  }

  /**
   * Select a date on the calendar to activate the length of stay dropdown.
   * @param selectedDate the date selected by the user.
   */
  selectNights(day: dayjs.Dayjs): void {
    const selected = this.prepareDate(day);
    const nights = this.generateNights(selected);
    this.setState({
      selectableDates: [],
      selected,
      selectableNights: nights,
      selectedNights: 0,
      selectedPrice: null,
      selectablePrices: this.generateFilteredPrices(selected),
    });
  }

  prepareDate(date: dayjs.Dayjs): string {
    return date.format('YYYY-MM-DD');
  }

  /**
   * Select a range of dates on the calendar.
   * @param selectedDate the date selected by the user.
   */
  selectRange(selectedDate: dayjs.Dayjs): void {
    const oldDate = this.state.selected;
    const hasSelectedDay = oldDate.length > 0;
    let selected = this.prepareDate(selectedDate);

    if (hasSelectedDay && oldDate != selected && this.state.selectedNights == 0) {
      // User selects end date
      const startDate = dayjs(oldDate)
      const nights = Math.abs(selectedDate.diff(startDate, 'days'))
      const price = this.generatePrice(nights);

      // Set earliest date to start date
      selected = startDate.isBefore(selectedDate, 'days') ? oldDate : selected

      this.setState({
        selectableDates: [],
        selected,
        selectedNights: nights,
        selectedPrice: price,
        selectablePrices: [],
      });
    } else {
      const nights = this.filterPriceByDate(selected)
      this.getValidEndDates(selectedDate, nights).then(validEndDates => {
        this.setState({
          selected,
          selectedNights: 0,
          selectedPrice: null,
          selectableDates: validEndDates,
          selectablePrices: this.generateFilteredPrices(selected),
        });
      })
    }
  }

  generatePrice(night: number) {
    const price: Price = this.state.selectablePrices.filter((value) => {
      return value.nights === night;
    })[0] || null;
    return price;
  }

  onNightSelect(night: number): void {
    const price = this.generatePrice(night);
    this.setState({
      selectedNights: night,
      selectedPrice: price,
      selectablePrices: this.state.selectablePrices,
    });
  }

  generateNights(startDate: string): number[] {
    const { prices } = this.state;
    const filteredPrices = prices.filter((value) => {
      return value.start_date === startDate;
    });

    const nights: number[] = filteredPrices.map((value) => {
      return value.nights;
    });
    return nights
  }

  filterPriceByDate(startDate: string): number[] {
    const { prices } = this.state;
    const filteredPrices = prices.filter((value) => {
      return value.start_date === startDate;
    });

    this.setState({
      prices: filteredPrices
    })
    const nights: number[] = filteredPrices.map((value) => {
      return value.nights;
    });
    return nights
  }

  async getValidEndDates(startDate: dayjs.Dayjs, nights: number[]): Promise<string[]> {
    const usePricingApi = Config.get('useCalendarApi', 'no') == 'yes'

    if (usePricingApi) {
      return getEndDates(Config.get('propertyCode'),
        startDate,
        Config.get('calendarKey')
      ).then((data: EndDateResponse) => {
        let dates: string[] = []
        Object.values(data.dates).map((date) => {
          dates.push(date.dateTime)
        })
        return dates
      }
      )
    } else {
      const validEndDates: string[] = nights.map((night) => {
        return startDate.add(night, 'days').format('YYYY-MM-DD').toString();
      });
      return validEndDates
    }
  }

  openBookingForm(evt: React.MouseEvent, arrivalDate: dayjs.Dayjs, nights: number): void {
    evt.preventDefault();
    const departureDate = arrivalDate.add(nights, 'days')

    if (this.state.prices.length === 0) {
      const enquiryIframe = document.querySelector('[data-calendar-key] iframe') as HTMLIFrameElement;
      if (enquiryIframe?.contentWindow) {
        enquiryIframe.contentWindow.postMessage({
          action: 'cartOpen',
          arrivalDate: arrivalDate.format('DD/MM/YYYY'),
          departureDate: departureDate.format('DD/MM/YYYY'),
          numberNights: nights,
          capacity: Config.get('sleeps', '0'),
          cottageID: Config.get('propertyCode'),
        }, '*');
      }

      return 
    }

    window.postMessage({
      action: 'cartOpen',
      arrivalDate: arrivalDate.format('DD/MM/YYYY'),
      departureDate: departureDate.format('DD/MM/YYYY'),
      numberNights: nights,
      capacity: Config.get('sleeps', '0'),
      cottageID: Config.get('propertyCode'),
    }, '*');
  }

  resetDates = () => {
    this.setState({
      selectableDates: [],
      selectableNights: [],
      selected: '',
      selectedNights: 0,
      selectedPrice: null,
    })
  }

  render(): JSX.Element {
    const {
      start,
      notBooked,
      selected,
      selectedNights,
      selectedPrice,
      prices,
      loading,
      displayButton,
      alwaysDisplayNightSelector,
      nightsSelectorPosition,
      selectableDates,
      selectableNights,
      calendarsToDisplay,
      arrivals
    } = this.state;

    const containerClasses: string[] = [
      'calendar-container',
    ];

    if (loading) {
      this.loadPrices();
      containerClasses.push('calendar-container--loading');
    }

    if (selected && !selectedNights) {
      containerClasses.push('calendar-start-selected');
    }

    const selectable: string[] = Config.get('useCalendarApi', 'no') == 'yes' ? arrivals : prices.map((price) => {
      return price.start_date;
    });

    let rate: string = ''
    let buttonText = ''

    if (Config.get('useCalendarApi', 'no') == 'yes' && selected && ((!selectableDates.length && !selectedNights) || !selectedPrice)) {
      buttonText = Config.get('enquireText', 'Enquire Now')
    } else if (selectedPrice && Config.get('showPriceOnBookButton', 'yes') === 'yes') {
      rate = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        currencyDisplay: 'narrowSymbol',
      }).format(selectedPrice.rate)

      buttonText = Config.get('no-prices', false)
        ? 'Check Availability & Prices'
        : Config.get('bookNowText', 'Book now for') + ' ' + rate.replace('.00', '')
    } else {
      buttonText = Config.get('bookNowTextNoPrice', 'Book Now');
    }

    const checkoutUrl: string = Config.get('checkoutPage')
      ? Config.get('checkoutPage') + `?cottageId=${Config.get('propertyCode')}&startDate=${dayjs(this.state.selected).format('YYYY-MM-DD')}&nights=${this.state.selectedNights}`
      : getDeepLink(dayjs(selected, 'YYYY-MM-DD'), selectedNights);

    return <div className={containerClasses.join(' ')}>
      {loading && <Loader />}
      {Config.get('no-prices', false) && <><div className="no-price-overlay" /><div className="no-prices-msg" dangerouslySetInnerHTML={{ __html: Config.get('no-price-msg') }} /></>}

      {(nightsSelectorPosition == 'top' && (alwaysDisplayNightSelector || displayButton || selected.length > 0) && Config.get('calendar_selector', 'default') == 'default') &&
        <NightSelect
          selected={selectedNights}
          nights={selectableNights}
          onChange={this.onNightSelect}
        />
      }

      {[...Array.from(Array(calendarsToDisplay))].map((e, i) => (
        <div key={i} className="individual-calendar-wrapper">
          {i == 0 &&
            <MonthSelect
              month={start}
              monthsToDisplay={Config.get('monthsToDisplay', 18)}
              onChange={this.onMonthChange}
            />
          }
          {i >= 1 &&
            <p>{dayjs(start).add(i, 'months').format('MMMM YYYY')}</p>
          }

          <Calendar
            key={"calendar-view-" + i}
            start={dayjs(start).add(i, 'months').format('YYYY-MM')}
            notBooked={notBooked}
            selectable={selected && !selectedNights ? selectableDates : selectable}
            selected={selected}
            onDaySelect={this.onDaySelect}
            nights={selectedNights}
          />
        </div>
      ))}
      {(nightsSelectorPosition == 'bottom' && (alwaysDisplayNightSelector || displayButton || selected.length > 0) && Config.get('calendar_selector', 'default') == 'default') &&
        <NightSelect
          selected={selectedNights}
          nights={selectableNights}
          onChange={this.onNightSelect}
        />
      }
      {(selected && Config.get('reset_dates_button', 'no') == 'yes') && Config.get('reset_dates_location', 'under-calendar') == 'under-calendar' &&
        <button
          className='reset-dates'
          onClick={this.resetDates}>
          {Config.get('reset_dates_text', 'Reset Dates')}
        </button>
      }
      {
        Config.get('useKey') === "yes" &&
        Config.get("calendarKeyLocation", "under_calendar") === "under_calendar" &&
        <div id="calendar-key">
          <div id="booked-day">
            <span className="calendar__day calendar__day--key"></span><span className="key-text">Booked</span>
          </div>
          <div id="selected-day">
            <span className="calendar__day calendar__day--key calendar__day--not-booked calendar__day--selectedEnds"></span><span className="key-text">Selected</span>
          </div>
        </div>
      }
      {Config.get('usePriceInfo', 'no') === "yes" &&
        <div>
          <PriceInfo price={selectedPrice} hideSection={this.state.selected && !this.state.selectedNights} />
        </div>
      }
      {((selected && !selectableDates.length) || displayButton || selectedPrice || Config.get('no-prices', false)) &&
        <div className="calendar-price-display">
          <a
            className="calendar-price-display__book"
            onClick={(evt: React.MouseEvent) => {
              if (Config.get('checkoutPage', false))
                return

              if (Config.get('useLocalCheckout', false)) {
                evt.preventDefault()
                this.setState({
                  ...this.state,
                  showCheckout: true,
                })
              } else {
                this.openBookingForm(evt, dayjs(selected, 'YYYY-MM-DD'), selectedNights);
              }
            }}
            href={checkoutUrl}
          >
            {buttonText}
          </a>
        </div>
      }
      {selectedPrice && Config.get('price-msg', false) && <div className="under-calendar-text" dangerouslySetInnerHTML={{ __html: Config.get('price-msg') }} />}
      {this.state.showCheckout &&
        <Popup
          steps={[Step1, Step2]}
          title={'Your booking'}
          onClose={() => {
            dispatchEvent(document, 'gloversure.payment.popup.close')
            this.setState({
              ...this.state,
              showCheckout: false,
            })
          }}
          data={this.state.data}
          defaultStepData={{}}
          setData={(data) => {
            this.setState({
              ...this.state,
              data,
            })
          }}
        />
      }
      {(selected && Config.get('reset_dates_button', 'no') == 'yes') && Config.get('reset_dates_location', 'under-calendar') == 'bottom' &&
        <button
          className='reset-dates'
          onClick={this.resetDates}>
          {Config.get('reset_dates_text', 'Reset Dates')}
        </button>
      }
      {
        Config.get('useKey') === "yes" &&
        Config.get("calendarKeyLocation", "under_calendar") === "bottom" &&
        <div id="calendar-key">
          <div id="booked-day">
            <span className="calendar__day calendar__day--key"></span><span className="key-text">Booked</span>
          </div>
          <div id="selected-day">
            <span className="calendar__day calendar__day--key calendar__day--not-booked calendar__day--selectedEnds"></span><span className="key-text">Selected</span>
          </div>
        </div>
      }
    </div>;
  }
}

export default App;
